import axios from "axios";
import { ChatGptUserType, ConversationViewType } from 'containers/GPTChatBotContainer/useGPTChatBot';
import { StyledButton } from 'containers/QuestionCodingPane/Coding/AnswerCTA';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { evaluationPlatformService } from 'services';
import { submitResponseToAI } from 'services/evaluationPlatform';
import { RootState, useAppDispatch } from 'store';
import { setInterviewMeConversation, setShowInterviewMeBot } from 'store/splitView';
import styled from 'styled-components';
import { ISubmitResponseToAI, InterviewMeResponse } from 'types';
import { Candidate_Track_Id, DEFAULT_TOKEN, TrackEnrollType } from 'utilities/constants';

import { InterviewMeChatBox } from './InterviewMeChatBox';

import { Audio } from 'components/AudioRecorder/types/recorder';
import { withSecurityFeatures } from 'components/HigherOrderComponents/withSecurityFeatures';
import { useMessagePopup } from 'context/messagePopup';
import AnswerRecorder from 'pages/OnboardCandidate/Pitch/Components/AnswerRecorder';
import { getValueBrowserStorage } from 'services/browserStorageService';
import { getEnrollmentType } from 'utilities';
import { convertHttpsToHttpFromUrl } from 'utilities/commonUtils';
import InterviewMeHeader from './InterviewMeHeader';

const StyledChatContainer = styled.div`
    display: flex;
    background: white;
    transition: 1s;
    flex-direction: column;
    padding-left: 60px;
    padding-right: 25px;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 57px);
    position: absolute;
    width: 100%;
    z-index: 1000;
    bottom: 0;
    right: 0;
    .chat-window {
        display: flex;
        flex-direction: column;
        height: 90%;
        width: 100%;
        border-radius: 5px;
        background: white;
    }
    .chat-content {
    }
    .historyContainer {
        border-left: 2px solid RGB(200, 220, 255);
        border-right: 2px solid RGB(200, 220, 255);
        padding: 10px;
        overflow-y: scroll;
        flex-grow: 1;
    }
    .user-msg, .bot-msg {
        padding: 10px;
        border-radius: 5px;
        font-size: .9rem;
        position: relative;
        text-align: left;
        max-width: 80%;
    }
    .MuiOutlinedInput-adornedEnd {
        border: 1px solid RGB(200, 220, 255);
    }
    .bot-msg {
        background: linear-gradient(72deg, rgb(239, 245, 253) 0%, rgb(238, 238, 254) 79%);
        color: rgb(4, 3, 72);
    }
    .arrow-right, .arrow-left {
        width: 0;
        height: 0;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        position: absolute;
        top: 13px;
    }
    .arrow-left {
        left: -7px;
        border-right: 8px solid rgb(246, 248, 255);
    }
    .arrow-right {
        right: -7px;
        border-left: 8px solid #f1f1f1;
    }
    .user-msg {
        background: linear-gradient(108deg, rgb(246, 248, 255) 0%, rgb(220, 220, 220) 70%);
        color: rgb(60, 60, 60);
    }
    .bot-msg-container, .user-msg-container {
        display: flex;
        margin: 15px 0;
    }
    .bot-msg-container {
        justify-content: flex-start;
    }
    .user-msg-container {
        justify-content: flex-end;
    }
    .messaging-user-symbol, .messaging-bot-symbol {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        font-size: 1.2rem;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .messaging-user-symbol {
        margin-left: 12.5px;
        background-color: #d8d8d8;
    }
    .messaging-bot-symbol {
        margin-right: 12.5px;
        background-color: #3d4e8d;
        color: #fff;
    }
    .pre-line-white-space {
        white-space: pre-wrap;
    }
    .submit-wrapper {
        border: 1px solid RGB(200,220,255);
        padding: 9px;
    }
    .text-field {
        padding: 1px;
        margin-left: 4rem;
    }
    .MuiOutlinedInput-multiline {
        padding: 2px 10px;
        height: 60px;
    }
`;
export interface IConversationScore {
    score: number;
    capability: string;
}

export interface IConversation {
    id?: string;
    type: ChatGptUserType;
    text: string;
    answerAudioDetails?: {
        url: string;
        duration?: string;
    };
    score?: IConversationScore[];
    isError?: boolean;
    comments?: string;
    isHistory?: boolean;
    followUpQuestionId?: string;
    parentEvaluationId?: string;
    followUpLevelOrder?: number;
    maxSortOrder?: number;
    followUpQuestion?: string;
    questionAnswerId?: string;
    givenScore?: number;
    givenAnswerType?: ConversationViewType;
    hideAskExplanation?: boolean;
}

interface IInterviewMeChatbox {
    onSave: (conversation: IConversation[]) => void;
}

const InterviewMeContainer = ({ onSave }: IInterviewMeChatbox) => {
    const candidateTrackId = getValueBrowserStorage(Candidate_Track_Id)!;
    const isPlacement = getEnrollmentType() === TrackEnrollType.FORPLACEMENT;
    const dispatch = useAppDispatch();
    const endOfMessages = useRef<any>();
    const newAnswerFetched = useRef(false);
    const audioRef = useRef(null);
    const restartSpeechRecognition = useRef(false);
    // Removed inputMessage state
    const [audioStream, setAudioStream] = useState<Audio>();
    const [showAudioLoader, setShowAudioLoader] = useState(false);
    const audioStreamRef = useRef<Audio | undefined>();
    const updateAudioStream = (audio?: Audio) => {
        audioStreamRef.current = audio;
        setAudioStream(audio);
    };
    const { currentAnswerId, questionTitle, currentAnswer } = useSelector(
        (state: RootState) => state.splitView.bot
    );
    const [loadingBotResponse, setLoadingBotResponse] = useState<boolean>(false);
    const { conversation } = useSelector((state: RootState) => state.splitView.bot);
    const [lastBotResponseState, setLastBotResponseState] = useState<InterviewMeResponse>();
    const message = useMessagePopup();

    const scrollToBottom = useCallback(() => {
        endOfMessages?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [endOfMessages]);

    const handleSendMessage = useCallback((transcriptText: string, audioUrl?: string) => {
        if (audioRef && audioRef.current && (audioRef.current as any).isRecognizing()) {
            (audioRef.current as any).stopAudio();
            restartSpeechRecognition.current = true;
        } else {
            restartSpeechRecognition.current = false;
        }
        if (transcriptText.trim() === '') return;

        const newMessage = {
            type: ChatGptUserType.USER,
            text: transcriptText,
            ...(audioUrl && {
                answerAudioDetails: { url: audioUrl },
                givenAnswerType: ConversationViewType.AUDIO,
            }),
        };

        const updatedConversation = [...conversation, newMessage];

        let latestChatBotQuestion = null;
        for (let i = conversation.length - 1; i >= 0; i--) {
            const latestConversation = conversation[i];
            if (latestConversation.type === 0) {
                latestChatBotQuestion = latestConversation.text;
                break;
            }
        }
        const question = latestChatBotQuestion ?? questionTitle;
        dispatch(setInterviewMeConversation(updatedConversation));
        if (candidateTrackId && currentAnswerId && question && transcriptText) {
            interviewCandidate(candidateTrackId, currentAnswerId, question, transcriptText, updatedConversation);
        }
    }, [conversation, candidateTrackId, currentAnswerId, questionTitle, lastBotResponseState]);
    // Audio upload and transcript logic
    const handleAudioSubmit = () => {
        if (!audioStreamRef.current) {
            message.info("Please record your answer!");
            return;
        }
        setShowAudioLoader(true);
        handleUpload(audioStreamRef.current, (s3Path: string) => {
            evaluationPlatformService.fetchTranscriptFromAPI(s3Path).then(res => {
                const transcriptText = res.output?.transcriptionText;
                if (!transcriptText?.trim()) {
                    message.info("Transcript is empty or failed.");
                    setShowAudioLoader(false);
                    return;
                }
                updateAudioStream(undefined);
                handleSendMessage(transcriptText);
                setShowAudioLoader(false);
            }).catch(() => {
                message.fail("Transcript fetch failed.");
                setShowAudioLoader(false);
            });
        });
    };

    const handleUpload = (recording: Audio, cb: (url: string) => void) => {
        const file = recording.file;
        if (!file) return;
        const newFileName = file.name.replace(/ /g, '');
        const ext = file.type.split('/')[1];
        const type = file.type.split('/')[0];
        const reader = new FileReader();
        const path = `${candidateTrackId}/recordings/v1/${newFileName}~${type}~.${ext}`;

        reader.onloadend = () => {
            const buffer = Buffer.from(reader.result as any);
            evaluationPlatformService.getS3SignedInCredentials({ path, candidateTrackId }).then(res => {
                const url = res.output.url;
                axios.put(url, buffer).then(() => cb(path)).catch(() =>
                    axios.put(convertHttpsToHttpFromUrl(url), buffer).then(() => cb(path)).catch(err => console.error(err))
                );
            });
        };
        reader.readAsArrayBuffer(file);
    };

    // Removed handleTranscriptChange and handleInputChange

    const handleSubmitAnswer = useCallback(() => {
        message.confirm("This action will override your previous answer. Are you sure?", (response: any) => {
            onSave(conversation);
            dispatch(setShowInterviewMeBot(false));
        });
    }, [onSave, conversation, dispatch]);

    const handleRegenerateResponse = useCallback((message: IConversation) => {
        const updatedConversation = [...conversation];
        updatedConversation.pop();
        const lastUserMessage = updatedConversation[updatedConversation.length - 1];
        dispatch(setInterviewMeConversation(updatedConversation));

        let latestChatBotQuestion = null;
        const messageIdx = conversation.findIndex(botMsg => botMsg.isError === message.isError && botMsg.comments === message.comments);
        for (let i = messageIdx - 1; i >= 0; i--) {
            const latestConversation = conversation[i];
            if (latestConversation.type === 0) {
                latestChatBotQuestion = latestConversation.text;
                break;
            }
        }
        const question = latestChatBotQuestion ?? questionTitle;
        if (lastUserMessage && candidateTrackId && currentAnswerId && question) {
            interviewCandidate(candidateTrackId, currentAnswerId, question, lastUserMessage.text, updatedConversation);
        }
    }, [conversation, candidateTrackId, currentAnswerId, questionTitle, lastBotResponseState]);

    const interviewCandidate = useCallback(
        async (candidateTrackId: string, currentAnswerId: string, questionTitle: string, currentAnswer: string, conversation: IConversation[]) => {
            const payload: ISubmitResponseToAI = {
                token: DEFAULT_TOKEN,
                candidateTrackId: candidateTrackId,
                questionAnswerId: currentAnswerId,
                question: questionTitle,
                prompt: currentAnswer,
                answer: currentAnswer,
                temperature: 0.7,
                promptBuilderType: 'EVALUATION',
                parentEvaluationId: lastBotResponseState?.parentEvaluationId,
                followUpQuestionId: lastBotResponseState?.followUpQuestionId,
                levelOrder: 3,
                maxSortOrder: 0,
            };
            setLoadingBotResponse(true);
            let updatedConversation = [...conversation];
            try {
                const response = await submitResponseToAI(payload);
                const data = response.output as InterviewMeResponse;
                setLastBotResponseState(data);
                let comments = '';
                if (!data) {
                    throw ("No data received");
                }
                if (!data.followUpQuestion) {
                    comments = 'Thank you for the responses. Feel free to save your responses as the answer to this question.';
                }
                updatedConversation = [...conversation, { type: ChatGptUserType.BOT, text: data.followUpQuestion, score: isPlacement ? undefined : data.scores, comments }];
                dispatch(setInterviewMeConversation(updatedConversation));
            } catch (exception) {
                console.error(exception);
                let comments = `Error evaluating your response: \n`;
                updatedConversation = [...conversation, { type: ChatGptUserType.BOT, text: "", comments, isError: true }];
                dispatch(setInterviewMeConversation(updatedConversation));
            } finally {
                if (restartSpeechRecognition.current) {
                    (audioRef.current as any).startAudio();
                }
                setLoadingBotResponse(false);
            }
        },
        [lastBotResponseState, conversation]
    );

    const onClearConversation = () => {
        setLastBotResponseState(undefined);
    }

    useEffect(() => {
        if (conversation?.length > 0 && !lastBotResponseState) {
            const lastConversation = conversation[conversation.length - 1];
            if (lastConversation.followUpQuestionId && lastConversation.parentEvaluationId) {
                setLastBotResponseState({
                    questionAnswerId: lastConversation.questionAnswerId!,
                    followUpQuestion: lastConversation.followUpQuestion!,
                    followUpQuestionId: lastConversation.followUpQuestionId!,
                    maxSortOrder: lastConversation.maxSortOrder!,
                    parentEvaluationId: lastConversation.parentEvaluationId!,
                    score: lastConversation.givenScore!,
                    followUpLevelOrder: lastConversation.followUpLevelOrder!,
                    scores: lastConversation.score ?? [],
                    aiAnswer: "",
                });
            }
        }
    }, [conversation])

    useEffect(() => {
        scrollToBottom();
    }, [conversation, loadingBotResponse, scrollToBottom]);

    // Removed inputMessage useEffect

    useEffect(() => {
        if (!conversation.length) {
            currentAnswer && handleSendMessage(currentAnswer)
        }
    }, []);

    return (
        <StyledChatContainer>
            <div className="chat-window">
                <InterviewMeHeader onClearConversation={onClearConversation} />
                <>
                    <div className="historyContainer">
                        <InterviewMeChatBox
                            conversation={conversation}
                            loadingBotResponse={loadingBotResponse}
                            handleRegenerateResponse={handleRegenerateResponse}
                        />
                        <div ref={endOfMessages}></div>
                    </div>
                    <div className='footer bg-white d-flex align-items-center justify-content-end submit-wrapper'>
                        <div className='d-flex align-items-center justify-content-between w-100'>
                            <div className='w-100 d-flex'>
                                {showAudioLoader ? (
                                    <div className="w-100 d-flex align-items-center justify-content-center">Processing Audio...</div>
                                ) : (
                                    <AnswerRecorder
                                        recordedAudioStream={audioStream}
                                        setRecordedAudioStream={updateAudioStream}
                                        onSaveRecording={(recording, _, transcript) => {
                                            if (!transcript?.trim()) {
                                                message.info("Transcript is empty.");
                                                return;
                                            }
                                            const file = new File([recording.blob], recording.url, { type: recording.type });
                                            updateAudioStream({ key: recording.url, audio: recording.url, file });
                                        }}
                                        disableSubmitResponse={loadingBotResponse}
                                        onClickRespond={handleAudioSubmit}
                                        ctaLabel={'Respond'}
                                    />
                                )}
                            </div>
                            <div className='d-flex align-items-center flex-column justify-content-center'>
                                <StyledButton
                                    disabled={loadingBotResponse || conversation.findIndex(c => c.type === ChatGptUserType.USER) === -1}
                                    size='small'
                                    variant='contained'
                                    color='secondary'
                                    className='m-1'
                                    width="9rem"
                                    onClick={handleSubmitAnswer}
                                    title={'Save To Answer'}
                                >
                                    Save To Answer
                                </StyledButton>
                            </div>
                        </div>
                    </div>
                </>
            </div>
        </StyledChatContainer>
    );
};

export default withSecurityFeatures(InterviewMeContainer);
